import { Injectable } from '@angular/core';
import { ExteriorOptions, MechanicalOptions, VehicleOption } from 'remarketing-angular-library';
import { SubGroupName, subGroupMappings, SubGroupResponseName } from '../data/option-sub-group-mapping';
import {UntypedFormGroup} from '@angular/forms';
import {
  find as _find,
  set as _set,
  cloneDeep as _cloneDeep
} from 'lodash';

@Injectable()
export class OptionsAndAccessoriesFormsService {

  // Combines an array of option arrays with the originally present options, and cleans up the list
  getInitialOptions(options: VehicleOption[][], originalOptions: VehicleOption[]) {
    let otherOptions = [];
    options.forEach(o => {
      otherOptions = otherOptions.concat(o);
    });
    originalOptions?.forEach(o => {
      o.installed = false;
      otherOptions.push(o);
    });
    return this.cleanOptionsList(otherOptions);
  }

  // Ensures that the options list for the vehicle is comprised of unique options
  cleanOptionsList(optionsList: VehicleOption[]) {
    const uniqueOptions: VehicleOption[] = [];
    // Only use options that should be displayed in Additional Options and are unique in the list
    optionsList.forEach(o => {
      if(o) {
        if (subGroupMappings.filter(x => x.subGroupName === o.subGroupName).length > 0 &&
          uniqueOptions.filter(x => x.optionCode === o.optionCode).length === 0
        ) {
          uniqueOptions.push(o);
        }
      }
    });
    return uniqueOptions;
  }

  filterBySubGroup(options: VehicleOption[], name: SubGroupName){
    return options.filter(o=>o.subGroupName === name);
  }

  // Sets up a base array of vehicle options, as well as a display array with separate entries for aftermarket values
  configureDropdownList(baseOptions: VehicleOption[], displayOptions: VehicleOption[], additionalOptions?: VehicleOption[]) {
    const options = additionalOptions ? additionalOptions.concat(baseOptions) : baseOptions;

    options?.forEach(x => {
      // Set up the standard option
      const standardOption = _cloneDeep(x);

      // Our standard options can't be aftermarket, and it should only be marked as installed if the BE data isn't aftermarket
      standardOption.afterMarket = false;
      if (x.installed && x.afterMarket) {
        standardOption.installed = false;
      }
      displayOptions.push(standardOption);
      // Set up the aftermarket option
      if (x.allowAfterMarket) {
        const amOption = _cloneDeep(x);
        amOption.platformCustomDescription += ' (AM)';
        amOption.afterMarket = true;
        displayOptions.push(amOption);
      }
    });
  }

  handleVehicleOptionDropdown(form: UntypedFormGroup, formKey: string, options: VehicleOption[], displayOptions: VehicleOption[]) {
    const selectedDisplayOption = _find(displayOptions,
      {
        optionCode: form.get(formKey).value.optionCode,
        afterMarket: form.get(formKey).value.afterMarket
      });
    _set(_find(options, {installed: true}), 'installed', false);
    _set(_find(options, {optionCode: form.get(formKey).value.optionCode}), 'installed', true);
    if (selectedDisplayOption) {
      _set(_find(options, {optionCode: form.get(formKey).value.optionCode}), 'afterMarket', selectedDisplayOption.afterMarket);
    }
  }

  // Sets the selected option in case of show all options.
  modifyOriginalVehicleOptions(vehicleOptions: VehicleOption[], installedOption: VehicleOption) {
    return vehicleOptions.map(opt => opt.optionCode === installedOption.optionCode ?
      {...opt, installed: true, afterMarket: installedOption.afterMarket} : {...opt, installed: false});
  }

  // For show all options, this function updates the selected option (if any) from previous state/when show all is unchecked in all options list.
  setExpandedOptionsDropdownList(subgroup: SubGroupName, optionType: string, vehicleOption: VehicleOption[], vehicleDisplayOption: VehicleOption[],
    postingOptionsArray: ExteriorOptions | MechanicalOptions, additionalOptions: VehicleOption[]) {
    const updatedAdditionalOptions = (postingOptionsArray[optionType] || []).concat(additionalOptions);
    const cleanedList = this.cleanOptionsList(updatedAdditionalOptions);
    const allOptions = this.filterBySubGroup(cleanedList, subgroup);
    const selectedOption = postingOptionsArray[optionType]?.find(opt => opt.installed);
    const modifiedOptions = selectedOption ? this.modifyOriginalVehicleOptions(allOptions, selectedOption) : allOptions;
    postingOptionsArray[optionType] = modifiedOptions;
    this.configureDropdownList(vehicleOption, vehicleDisplayOption, modifiedOptions);
  }

  // This function will add the selected option from show all list which is not present in the non-expanded list.
  setBaseOptionsDropdownList(optionType: string, originalVehicleOptions: VehicleOption[], postingOptionsArray: ExteriorOptions | MechanicalOptions) {
    const installedOption = postingOptionsArray[optionType].find(opt => opt.installed);
    if (installedOption) {
      const originalOptions = originalVehicleOptions?.map(opt =>
        opt.installed ? { ...opt, installed: false, afterMarket: installedOption.afterMarket } : opt );
      postingOptionsArray[optionType] = originalOptions ? originalOptions : [];
      const idx = postingOptionsArray[optionType]?.findIndex(opt => opt.optionCode === installedOption.optionCode);
      if (idx === -1 ) {
        postingOptionsArray[optionType].push(installedOption);
      } else {
        postingOptionsArray[optionType][idx] = installedOption;
      }
    } else {
      postingOptionsArray[optionType] = originalVehicleOptions ? originalVehicleOptions : [];
    }
  }

  // Removes the non-selected display option of selected option (aftermarket or non-aftermarket) in non-expanded options list.
  filterVehicleDisplayOption(subGroup: SubGroupName, optionType: string, originalVehicleOptions: VehicleOption[], vehicleDisplayOption: VehicleOption[],
    postingOptionsArray: ExteriorOptions | MechanicalOptions, additionalOptions: VehicleOption[]) {
    const allOptions = this.filterBySubGroup(additionalOptions, subGroup);
    const installedOption = postingOptionsArray[optionType].find(opt => opt.installed);
    const installedOptIdx = installedOption ? originalVehicleOptions?.findIndex(opt => opt.optionCode === installedOption.optionCode) : 0;
    const filterDisplayOption = installedOptIdx === -1 ? this.filterDisplayOption(allOptions, installedOption) : undefined;
    if (filterDisplayOption && filterDisplayOption.allowAfterMarket) {
      const displayOptIdx = vehicleDisplayOption.findIndex(opt => opt.optionCode === filterDisplayOption.optionCode && opt.afterMarket === filterDisplayOption.afterMarket);
      vehicleDisplayOption.splice(displayOptIdx, 1);
    }
  }

  filterDisplayOption(expandedOptions: VehicleOption[], selectedOption: VehicleOption) {
    const selectedOptionFromAllOptions = expandedOptions.find(opt => opt.optionCode === selectedOption.optionCode);
    return {...selectedOptionFromAllOptions, afterMarket: !selectedOption.afterMarket};
  }

  setVehicleOptionsForNotShowAll(selectedOption: VehicleOption, optionType: SubGroupResponseName, originalVehicleOptions: VehicleOption[],
    postingOptionsArray: ExteriorOptions | MechanicalOptions) {
    postingOptionsArray[optionType] = originalVehicleOptions?.map(opt =>
      opt.optionCode === selectedOption?.optionCode ? {...opt, installed: selectedOption?.installed, afterMarket: selectedOption?.afterMarket} : opt
    );
  }
}
